import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Mic,
  MicOff,
  PhoneOff,
  User,
  Users,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  SwitchCamera,
} from 'lucide-react';
import type { CallState } from '../../types';

interface ActiveCallModalProps {
  currentCall: CallState | null;
  localStream?: MediaStream | null;
  isVideoEnabled?: boolean;
  isLocalVideoLarge?: boolean;
  isBackCamera?: boolean;
  currentUserId?: number | null;
  connectionState?: RTCPeerConnectionState | null;
  callError?: string | null;
  onEnd: () => void;
  onToggleMute: () => void;
  onToggleVideo?: () => void;
  onToggleSpeaker?: () => void;
  onToggleLocalVideoSize?: () => void;
  onSwitchCamera?: () => void;
  onClearCallError?: () => void;
  isSpeakerOn?: boolean;
  ending?: boolean;
  setRemoteAudioElement?: (element: HTMLAudioElement | null) => void;
}

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function connectionLabel(state: RTCPeerConnectionState | null | undefined): string {
  switch (state) {
    case 'new':
      return 'Bağlantı hazırlanıyor';
    case 'connecting':
      return 'Bağlanıyor...';
    case 'connected':
      return 'Bağlandı';
    case 'disconnected':
      return 'Bağlantı kesildi';
    case 'failed':
      return 'Bağlantı hatası';
    case 'closed':
      return 'Kapalı';
    default:
      return 'Bağlanıyor...';
  }
}

export default function ActiveCallModal({
  currentCall,
  localStream,
  isVideoEnabled = true,
  isLocalVideoLarge = false,
  isBackCamera = false,
  currentUserId,
  connectionState,
  callError,
  onEnd,
  onToggleMute,
  onToggleVideo,
  onToggleSpeaker,
  onToggleLocalVideoSize,
  onSwitchCamera,
  onClearCallError,
  isSpeakerOn = false,
  ending = false,
  setRemoteAudioElement,
}: ActiveCallModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [localVideoElement, setLocalVideoElement] = useState<HTMLVideoElement | null>(null);
  const [remoteVideoElement, setRemoteVideoElement] = useState<HTMLVideoElement | null>(null);
  const localVideoRef = useCallback((node: HTMLVideoElement | null) => {
    setLocalVideoElement(node);
  }, []);
  const remoteVideoRef = useCallback((node: HTMLVideoElement | null) => {
    setRemoteVideoElement(node);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setRemoteAudioElement?.(audio);
    audio.srcObject = currentCall?.remoteStream ?? null;
    if (currentCall?.remoteStream) {
      audio.play().catch(() => {
        // Mobil tarayıcılarda autoPlay kısıtlaması durumunda sessizce devam et.
      });
    }
    return () => {
      setRemoteAudioElement?.(null);
    };
  }, [currentCall?.remoteStream, setRemoteAudioElement]);

  useEffect(() => {
    if (!remoteVideoElement) return;
    remoteVideoElement.srcObject = currentCall?.remoteStream ?? null;
    if (currentCall?.remoteStream) {
      remoteVideoElement.play().catch(() => {});
    }
  }, [currentCall?.remoteStream, remoteVideoElement]);

  useEffect(() => {
    if (!localVideoElement) return;
    localVideoElement.srcObject = localStream ?? null;
    if (localStream) {
      localVideoElement.play().catch(() => {});
    }
  }, [localStream, localVideoElement]);

  useEffect(() => {
    if (!currentCall) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentCall]);

  if (!currentCall) return null;

  const { call, isMuted, duration } = currentCall;
  const caller = call.caller;
  const isVideo = call.call_type === 'video';
  const callTypeLabel = isVideo ? 'Görüntülü arama' : 'Sesli arama';
  const remoteUser =
    currentUserId && caller && caller.id === currentUserId
      ? call.participants?.find((p) => p.user_id !== currentUserId)?.user
      : caller;
  const title = remoteUser ? `${remoteUser.name} ${remoteUser.surname}` : callTypeLabel;
  const isGroup = (call.participants?.length ?? 0) > 2;
  const joinedCount = call.participants?.filter((p) => p.status === 'joined').length ?? 0;
  const statusText =
    call.status === 'ringing'
      ? 'Aranıyor...'
      : call.status === 'ongoing'
      ? 'Arama devam ediyor'
      : '';

  const controlButtonBase =
    'flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full transition-colors active:scale-95';

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-[70] flex flex-col bg-slate-900 text-white sm:items-center sm:justify-center sm:bg-black/80 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="active-call-title"
      data-testid="active-call-modal"
    >
      <audio autoPlay playsInline muted={false} ref={audioRef} className="hidden" />

      <div
        className={`flex h-full w-full flex-col bg-slate-900 p-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:h-auto sm:min-h-[600px] sm:rounded-3xl sm:p-6 ${
          isVideo ? 'sm:max-w-3xl' : 'sm:max-w-md'
        }`}
      >
        {isVideo && (
          <div className="relative mb-3 flex-1 overflow-hidden rounded-2xl bg-black sm:min-h-[360px]">
            {/* Remote video: büyük ana alan veya küçük PIP. */}
            <button
              onClick={onToggleLocalVideoSize}
              className={`absolute overflow-hidden rounded-lg transition-all active:scale-95 ${
                isLocalVideoLarge
                  ? 'top-3 left-3 z-20 h-24 w-20 border-2 border-white/30 shadow-lg pointer-events-auto sm:top-4 sm:left-4 sm:h-32 sm:w-24'
                  : 'inset-0 z-10 rounded-none pointer-events-none'
              }`}
              aria-label={isLocalVideoLarge ? 'Karşı tarafı büyüt' : 'Karşı taraf küçük PIP'}
              data-testid="remote-video-container"
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
                data-testid="remote-video"
              />

              {!currentCall?.remoteStream && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-700 sm:h-12 sm:w-12">
                    {isGroup ? <Users className="h-5 w-5 sm:h-6 sm:w-6" /> : <User className="h-5 w-5 sm:h-6 sm:w-6" />}
                  </div>
                  <span className="text-[10px] sm:text-xs">Karşı taraf henüz katılmadı</span>
                </div>
              )}
            </button>

            {/* Local video: küçük PIP veya büyük ana alan. */}
            <button
              onClick={onToggleLocalVideoSize}
              className={`absolute overflow-hidden rounded-lg border-2 border-white/30 shadow-lg transition-all active:scale-95 ${
                isLocalVideoLarge
                  ? 'inset-0 z-10 rounded-none border-0 pointer-events-none'
                  : 'bottom-3 right-3 z-20 h-24 w-20 pointer-events-auto sm:bottom-4 sm:right-4 sm:h-32 sm:w-24'
              }`}
              aria-label={isLocalVideoLarge ? 'Küçük görünüme dön' : 'Kendi görüntünü büyüt'}
              data-testid="local-video-container"
            >
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className={`h-full w-full bg-black object-cover ${
                  isVideoEnabled && !isBackCamera ? 'scale-x-[-1]' : ''
                }`}
                data-testid="local-video"
              />
              {!isVideoEnabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-[10px] text-white sm:text-xs">
                  Kamera kapalı
                </div>
              )}
            </button>
          </div>
        )}

        {callError && (
          <div
            className="mb-3 flex items-center justify-between rounded-lg bg-red-500/20 px-3 py-2 text-xs text-red-100 sm:text-sm"
            data-testid="call-error-message"
          >
            <span className="flex-1">{callError}</span>
            {onClearCallError && (
              <button
                onClick={onClearCallError}
                aria-label="Hatayı kapat"
                className="ml-2 rounded p-1 hover:bg-red-500/30"
              >
                ✕
              </button>
            )}
          </div>
        )}

        <div
          className={`flex flex-1 flex-col items-center justify-center text-center ${
            isVideo ? 'flex-none' : ''
          }`}
        >
          {!isVideo && (
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-slate-700 ring-4 ring-slate-600 sm:mb-4 sm:h-20 sm:w-20">
              {isGroup ? (
                <Users className="h-8 w-8 text-slate-300 sm:h-10 sm:w-10" />
              ) : (
                <User className="h-8 w-8 text-slate-300 sm:h-10 sm:w-10" />
              )}
            </div>
          )}

          <h2 id="active-call-title" className="text-lg font-bold sm:text-xl">
            {title}
          </h2>

          <div className="mt-1 text-xs font-medium text-slate-400" data-testid="active-call-type-label">
            {callTypeLabel}
          </div>

          <div className="mt-1 flex items-center gap-2 text-xs text-slate-300 sm:text-sm">
            {call.status === 'ringing' && (
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-green-400" />
            )}
            <span>{statusText}</span>
          </div>

          {call.status === 'ongoing' && (
            <>
              <div
                className="mt-1 text-xl font-medium tabular-nums tracking-wide sm:mt-2 sm:text-2xl"
                aria-live="polite"
              >
                {formatDuration(duration)}
              </div>
              {connectionLabel(connectionState) && (
                <div className="mt-0.5 text-xs text-slate-400" data-testid="call-connection-state">
                  {connectionLabel(connectionState)}
                </div>
              )}
            </>
          )}

          {isGroup && (
            <p className="mt-2 text-xs text-slate-400 sm:mt-3">
              {joinedCount} / {call.participants?.length ?? 0} katılımcı
            </p>
          )}

          {call.participants && call.participants.length > 0 && (
            <div className="mt-4 w-full max-w-xs sm:mt-6">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {call.participants.slice(0, 6).map((p) => {
                  const u = p.user;
                  const initials = u ? `${u.name?.[0] ?? ''}${u.surname?.[0] ?? ''}` : '?';
                  return (
                    <div
                      key={p.id}
                      className="flex items-center gap-2 rounded-full bg-slate-800 px-2 py-1 text-[10px] sm:px-2.5 sm:py-1.5 sm:text-xs"
                      title={u ? `${u.name} ${u.surname}` : 'Bilinmeyen'}
                    >
                      {u?.avatar ? (
                        <img src={u.avatar} alt="" className="h-4 w-4 rounded-full object-cover sm:h-5 sm:w-5" />
                      ) : (
                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold sm:h-5 sm:w-5 sm:text-[10px]">
                          {initials || '?'}
                        </span>
                      )}
                      <span className="max-w-[5rem] truncate sm:max-w-[6rem]">
                        {u ? `${u.name} ${u.surname}` : 'Bilinmeyen'}
                      </span>
                    </div>
                  );
                })}
                {call.participants.length > 6 && (
                  <span className="rounded-full bg-slate-800 px-2 py-1 text-[10px] text-slate-400 sm:px-2.5 sm:text-xs">
                    +{call.participants.length - 6}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-auto grid grid-cols-5 place-items-center gap-2 sm:mb-2 sm:gap-4">
          <button
            onClick={onToggleMute}
            aria-label={isMuted ? 'Mikrofonu aç' : 'Mikrofonu kapat'}
            data-testid="mute-call-button"
            className={`${controlButtonBase} ${
              isMuted
                ? 'bg-red-500/20 text-red-400'
                : 'bg-slate-700 text-white hover:bg-slate-600'
            }`}
          >
            {isMuted ? <MicOff className="h-5 w-5 sm:h-6 sm:w-6" /> : <Mic className="h-5 w-5 sm:h-6 sm:w-6" />}
          </button>

          {isVideo && onToggleVideo && (
            <button
              onClick={onToggleVideo}
              aria-label={isVideoEnabled ? 'Kamerayı kapat' : 'Kamerayı aç'}
              data-testid="toggle-video-button"
              className={`${controlButtonBase} ${
                isVideoEnabled
                  ? 'bg-slate-700 text-white hover:bg-slate-600'
                  : 'bg-red-500/20 text-red-400'
              }`}
            >
              {isVideoEnabled ? (
                <Video className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <VideoOff className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </button>
          )}

          {isVideo && onSwitchCamera && (
            <button
              onClick={onSwitchCamera}
              aria-label="Kamerayı değiştir"
              data-testid="switch-camera-button"
              className={`${controlButtonBase} bg-slate-700 text-white hover:bg-slate-600`}
            >
              <SwitchCamera className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          )}

          {onToggleSpeaker && (
            <button
              onClick={() => {
                console.log('[ActiveCallModal] speaker button clicked, onToggleSpeaker exists:', !!onToggleSpeaker);
                onToggleSpeaker();
              }}
              aria-label={isSpeakerOn ? 'Hoparlör açık, kulaklığa geç' : 'Kulaklık modu, hoparlöre geç'}
              title={isSpeakerOn ? 'Hoparlör açık' : 'Kulaklık modu'}
              data-testid="speaker-call-button"
              className={`${controlButtonBase} ${
                isSpeakerOn ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-white hover:bg-slate-600'
              }`}
            >
              {isSpeakerOn ? (
                <Volume2 className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <VolumeX className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </button>
          )}

          <button
            onClick={onEnd}
            disabled={ending}
            aria-label="Aramayı sonlandır"
            data-testid="end-call-button"
            className="col-start-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-colors hover:bg-red-700 disabled:opacity-50 active:scale-95 sm:h-16 sm:w-16"
          >
            <PhoneOff className="h-6 w-6 sm:h-7 sm:w-7" />
          </button>
        </div>

        <div className="mt-2 text-center text-[10px] text-slate-400 sm:text-xs">
          {ending ? 'Sonlandırılıyor...' : callTypeLabel}
        </div>
      </div>
    </div>
  );
}
